import React, {useState, useEffect, useMemo, useCallback, useRef} from "react";
import {
    Box, Container, Alert, Typography, Stack, LinearProgress, ToggleButtonGroup, ToggleButton, Button, GlobalStyles, Snackbar,
    useMediaQuery,
} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {useGridApiRef} from "@mui/x-data-grid";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import MainLayout from "../../MainLoyout/MainLayout";
import Api from "../../../Services/Api";
import HeaderBar, {PhoneHeader, PHONE_HEADER_H, PHONE_TABS_H} from "./HeaderBar";
import OverviewBand, {SeriesToggle, readSeries} from "./OverviewBand";
import FeedPanel from "./FeedPanel";
import LaunchesPanel from "./LaunchesPanel";
import TodayLine from "./TodayLine";
import Heatmap from "./Heatmap";
import GroupsPanel from "./GroupsPanel";
import TagGrid, {TABLE_HELP} from "./TagGrid";
import {RangeCaption} from "./KpiStrip";
import {HelpPopover} from "./cells";
import TagListMobile, {SORT_KEYS, SortChip} from "./TagListMobile";
import {HEAT_METRICS} from "./Heatmap";
import PhoneTriage from "./PhoneTriage";
import {ResyncButton, PullBanner, pullLabel, isActivePull} from "./ResyncControl";
import {OpenAlarmsCard} from "./FeedPanel";
import FilterSheet from "./FilterSheet";
import TagDrawer from "./TagDrawer";
import {
    ratio, presetRange, severityOf, severityRank, atRisk, shortProvider, isNilValue, fmtInt, fmtMoneyShort,
    ALARM_STATUSES, INFO_STATUSES, DAY_METRICS, readCompareBase,
} from "./format";
import {buildFeed} from "./feed";
import {TYPE, TABULAR, NEUTRAL, CONTROL_H} from "./tokens";

const SUM_KEYS = ["fs", "mon", "clicks", "rev", "net", "rev_prev", "rev_last"];

/**
 * Sum the additive metrics of `rows`, then derive the ratios (sums first, ratios after; ÷0 → null). `withDaily` adds the
 * day-by-day revenue sum plus what the Networks strip needs to grey a misleading Δ: stopped tags, all-new run, Δ yday.
 */
const rollup = (rows, withDaily = false) => {
    const acc = {fs: 0, mon: 0, clicks: 0, rev: 0, net: 0, rev_prev: 0, rev_last: 0, tags: rows.length};
    rows.forEach((r) => SUM_KEYS.forEach((k) => { acc[k] += Number(r[k]) || 0; }));
    if (withDaily) {
        const base = rows.find((r) => Array.isArray(r.daily) && r.daily.length);
        acc.daily = base ? base.daily.map((d, i) => ({day: d.day, rev: rows.reduce((t, r) => t + ((r.daily && r.daily[i] && Number(r.daily[i].rev)) || 0), 0)})) : [];
        acc.stopped = rows.filter((r) => r.status === "STOPPED" || r.status === "DEAD").length;
        const earning = rows.filter((r) => r.rev > 0);
        acc.newRun = earning.length > 0 && earning.every((r) => r.yesterday && (r.yesterday.short_history || r.yesterday.state === "new"));
        let yRev = 0; let yBase = 0;
        rows.forEach((r) => { const y = r.yesterday; if (y && !isNilValue(y.base_rev) && y.state !== "pending") { yRev += Number(y.rev) || 0; yBase += Number(y.base_rev) || 0; } });
        acc.yday_delta_pct = yBase > 0 ? ((yRev - yBase) / yBase) * 100 : null;
    }
    acc.coverage = ratio(acc.mon, acc.fs);
    acc.ctr = ratio(acc.clicks, acc.mon);
    acc.rpm = acc.mon > 0 ? (acc.rev / acc.mon) * 1000 : null;
    acc.rpm_fs = acc.fs > 0 ? (acc.rev / acc.fs) * 1000 : null;
    acc.delta_rev_pct = acc.rev_prev > 0 ? ((acc.rev - acc.rev_prev) / acc.rev_prev) * 100 : null;
    return acc;
};

const median = (xs) => {
    const s = [...xs].sort((a, b) => a - b);
    const m = s.length >> 1;
    return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const matchesSearch = (row, needle) => {
    if (!needle) return true;
    const hay = `${row.tag || ""} ${row.site || ""} ${row.provider || ""} ${row.supply_feed_name || ""}`.toLowerCase();
    return hay.includes(needle);
};

const SORT_LABEL = {
    sev: "severity", tag: "tag", site: "site", provider: "partner", fs_day: "sent/day", mon_day: "monetized/day", coverage: "coverage",
    clicks_day: "clicks/day", ctr: "CTR", rpm: "RPM", rpm_fs: "$/1k sent", idx: "× network", rev: "revenue", rev_last: "last day", delta_rev_pct: "Δ prev",
    yest_base: "Δ yday vs 7d avg", yest_d1: "Δ yday vs day before",
};

const readLocal = (key, allowed, fallback) => {
    try {
        const v = window.localStorage.getItem(key);
        if (allowed.includes(v)) return v;
    } catch (e) { /* ignore */ }
    return fallback;
};
const writeLocal = (key, v) => { try { window.localStorage.setItem(key, v); } catch (e) { /* ignore */ } };
const VIEW_KEYS = ["panel", "heatmap"];
const HM_METRICS = ["rev", "net", "rpm_fs", "coverage", "fs", "rpm"];

const readSort = () => {
    try {
        const v = JSON.parse(window.localStorage.getItem("tc.sort") || "null");
        if (v && SORT_KEYS.some((k) => k.key === v.key) && (v.dir === "asc" || v.dir === "desc")) return v;
    } catch (e) { /* ignore */ }
    return {key: "rev", dir: "desc"};
};

const DEFAULT_SORT_MODEL = [{field: "rev", sort: "desc"}];
const PAGE_SIZE = 50;
const DEFAULT_PAGINATION = {page: 0, pageSize: PAGE_SIZE};

const TrafficControl = () => {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"), {noSsr: true});
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"), {noSsr: true});
    const isMid = useMediaQuery(theme.breakpoints.between("md", "lg"), {noSsr: true});
    const hideRpm = useMediaQuery("(max-width:1023px)", {noSsr: true});
    const variant = isPhone ? "phone" : isTablet ? "tablet" : isMid ? "mid" : "desktop";
    const stackPanels = isPhone || isTablet || isMid;

    // Preset range computed at mount, not at bundle load: a SPA tab open since an earlier day kept asking for that day's window.
    const [state, setState] = useState(() => {
        const r = presetRange(7);
        return {
            preset: 7, from: r.from, to: r.to,
            search: "", groups: [], providers: [], statuses: [], includeInactive: false,
            trendMode: "bars", selectedTag: null, groupBy: "status",
        };
    });
    const patch = useCallback((p) => setState((s) => ({...s, ...p})), []);
    // A preset follows the UTC day: re-derive it when the tab comes back and every few minutes while it stays open.
    useEffect(() => {
        const roll = () => setState((s) => {
            if (!s.preset) return s;
            const r = presetRange(s.preset);
            return r.from === s.from && r.to === s.to ? s : {...s, from: r.from, to: r.to};
        });
        roll();
        const onVisible = () => { if (document.visibilityState === "visible") roll(); };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", roll);
        const id = window.setInterval(roll, 5 * 60 * 1000);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", roll);
            window.clearInterval(id);
        };
    }, []);
    // v3 page state persisted per browser: view (Panel | Heatmap), money shown, table folded, heatmap options, last visit.
    const [view, setViewState] = useState(() => readLocal("tc.view", VIEW_KEYS, "panel"));
    const setView = useCallback((v) => { setViewState(v); writeLocal("tc.view", v); }, []);
    const [phoneView, setPhoneView] = useState("triage");
    const [hmSheet, setHmSheet] = useState(false);
    const [series, setSeriesState] = useState(readSeries);
    const setSeries = useCallback((v) => { setSeriesState(v); writeLocal("tc.overviewSeries", v); }, []);
    const [tableOpen, setTableOpenState] = useState(() => readLocal("tc.tableOpen", ["1", "0"], "0") === "1");
    const setTableOpen = useCallback((v) => { setTableOpenState(v); writeLocal("tc.tableOpen", v ? "1" : "0"); }, []);
    const [hmMetric, setHmMetricState] = useState(() => readLocal("tc.hmMetric", HM_METRICS, "rev"));
    const setHmMetric = useCallback((v) => { setHmMetricState(v); writeLocal("tc.hmMetric", v); }, []);
    const [hmMode, setHmModeState] = useState(() => readLocal("tc.hmMode", ["own", "d1"], "own"));
    const setHmMode = useCallback((v) => { setHmModeState(v); writeLocal("tc.hmMode", v); }, []);
    // Manual re-sync (v3.1): recent requests, polled every 10 s while one is queued / running; a finished one refetches without cache.
    const [pulls, setPulls] = useState([]);
    const [resyncPrefill, setResyncPrefill] = useState(null);
    const [nocache, setNocache] = useState(false);
    const [toast, setToast] = useState(null);   // "Bid Tags · Sep 13 · +$45" once a request finishes
    const activePulls = pulls.some(isActivePull);
    const loadPulls = useCallback(async () => {
        const res = await Api.trafficControlPulls();
        if (res && res.success && Array.isArray(res.requests)) {
            setPulls((prev) => {
                const wasActive = new Set(prev.filter(isActivePull).map((p) => p.id));
                const finished = res.requests.filter((p) => wasActive.has(p.id) && !isActivePull(p));
                if (finished.length) {
                    setToast(finished.map(pullLabel).join(" · "));
                    setNocache(true);
                    setRefreshKey((k) => k + 1);
                }
                return res.requests;
            });
        }
    }, []);
    useEffect(() => { loadPulls(); }, [loadPulls]);
    useEffect(() => {
        if (!activePulls) return undefined;
        const t = setInterval(loadPulls, 10000);
        return () => clearInterval(t);
    }, [activePulls, loadPulls]);
    const requestResync = useCallback(async (provider, day) => {
        const res = await Api.trafficControlPull({provider, day});
        if (res && res.success) loadPulls();
        return res;
    }, [loadPulls]);
    const asOfRef = useRef(null);   // overview.as_of, for contextual re-syncs without a day
    const resyncFor = useCallback((provider, day) => setResyncPrefill({provider, day: day || asOfRef.current}), []);
    /** `as_of` of the previous visit: the feed marks what is newer than that; written once the data has loaded. */
    const [lastSeen] = useState(() => { try { return window.localStorage.getItem("tc.lastSeen") || null; } catch (e) { return null; } });
    /** Baseline of every Δd on the page: "base" (7-day mean) or "d1" (day before). */
    const [compareBase, setCompareBaseState] = useState(readCompareBase);
    const setCompareBase = useCallback((v) => {
        setCompareBaseState(v);
        try { window.localStorage.setItem("tc.compareBase", v); } catch (e) { /* ignore */ }
    }, []);
    const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION);
    const [sort, setSortState] = useState(readSort);
    const setSort = useCallback((v) => {
        setSortState(v);
        try { window.localStorage.setItem("tc.sort", JSON.stringify(v)); } catch (e) { /* ignore */ }
    }, []);
    const [sortModel, setSortModel] = useState(DEFAULT_SORT_MODEL);
    const [filterOpen, setFilterOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const requestId = useRef(0);
    const apiRef = useGridApiRef();

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(state.search.trim().toLowerCase()), 250);
        return () => clearTimeout(t);
    }, [state.search]);

    // One fetch per (from, to, include_inactive). Everything else filters client-side.
    const {from, to, includeInactive} = state;
    useEffect(() => {
        if (!from || !to) return undefined;
        const id = ++requestId.current;
        setLoading(true);
        setError(null);
        const fresh = nocache;
        if (fresh) setNocache(false);
        Api.trafficControl({from, to, ...(includeInactive ? {include_inactive: 1} : {}), ...(fresh ? {nocache: 1} : {})}).then((res) => {
            if (id !== requestId.current) return; // stale
            try {
                if (res && res.success && Array.isArray(res.rows)) {
                    setData(res);
                    if (res.overview && res.overview.as_of) writeLocal("tc.lastSeen", res.overview.as_of);
                    const r = res.range || {};
                    setState((s) => {
                        const next = {...s};
                        if (r.from && r.to && (r.from !== s.from || r.to !== s.to)) { next.from = r.from; next.to = r.to; }
                        const prefixes = new Set(res.rows.map((x) => x.type_label || "Unassigned"));
                        const provs = new Set(res.rows.map((x) => x.provider));
                        next.groups = s.groups.filter((p) => prefixes.has(p));
                        next.providers = s.providers.filter((p) => provs.has(p));
                        return next;
                    });
                } else {
                    setError((res && (res.message || res.error)) || "Could not load traffic control data.");
                }
            } catch (e) {
                // Never leave the page on the loading state: surface the problem instead.
                setError(`Could not render traffic control data (${e && e.message ? e.message : e}).`);
            }
            setLoading(false);
        }, (e) => {
            if (id !== requestId.current) return;
            setError(`Could not load traffic control data (${e && e.message ? e.message : "network error"}).`);
            setLoading(false);
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [from, to, includeInactive, refreshKey]);

    // ------------------------------------------------------------------------------------------------ derived data
    const range = data && data.range;
    const rangeDays = (range && range.days) || 1;
    const days = useMemo(() => {
        const sample = data && data.rows && data.rows.find((r) => Array.isArray(r.daily) && r.daily.length);
        if (sample) return sample.daily.map((d) => d.day);
        if (!range) return [];
        const out = [];
        for (let d = dayjs.utc(range.from); !d.isAfter(dayjs.utc(range.to)); d = d.add(1, "day")) out.push(d.format("YYYY-MM-DD"));
        return out;
    }, [data, range]);

    /** Rows enriched once per response: severity, money at risk, index vs group median, per-metric means (heat). */
    const rows = useMemo(() => {
        const raw = (data && data.rows) || [];
        const peers = {};
        raw.forEach((r) => {
            if (r.rev >= 10 && r.fs > 0 && !isNilValue(r.rpm_fs)) (peers[r.prefix] = peers[r.prefix] || []).push(Number(r.rpm_fs));
        });
        const med = {};
        Object.keys(peers).forEach((p) => { if (peers[p].length >= 3) med[p] = median(peers[p]); });
        return raw.map((r) => {
            const sev = severityOf(r.status);
            const risk = atRisk(r);
            const earning = (r.daily || []).filter((d) => Number(d.rev) > 0);
            const means = {};
            DAY_METRICS.forEach((m) => {
                const vals = earning.map((d) => Number(d[m.key])).filter((v) => !Number.isNaN(v));
                means[m.key] = vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
            });
            const idx = med[r.prefix] && r.fs > 0 && !isNilValue(r.rpm_fs) ? Number(r.rpm_fs) / med[r.prefix] : null;
            return {
                ...r, _sev: sev, _sevKey: severityRank(r.status) + 1 - Math.min(risk, 999999) / 1e6, _atRisk: risk, _idx: idx,
                _earningDays: earning.length, _means: means, _provider: shortProvider(r.provider),
            };
        });
    }, [data]);

    /** Channels = the demand feed type of each tag's primary feed (`type_label`); `prefix` here is the channel key used by the filter. */
    const channelOf = (r) => r.type_label || "Unassigned";
    const groupOptions = useMemo(() => {
        const by = new Map();
        rows.forEach((r) => { const k = channelOf(r); if (!by.has(k)) by.set(k, {prefix: k, label: k, code: r.type || null, rev: 0}); by.get(k).rev += Number(r.rev) || 0; });
        return Array.from(by.values()).sort((a, b) => b.rev - a.rev);
    }, [rows]);
    const providerOptions = useMemo(() => Array.from(new Set(rows.map((r) => r.provider).filter(Boolean))).sort(), [rows]);

    const {groups, providers, statuses} = state;
    const baseRows = useMemo(() => rows.filter((r) =>
        (providers.length === 0 || providers.includes(r.provider)) && matchesSearch(r, debouncedSearch)), [rows, providers, debouncedSearch]);
    const filteredRows = useMemo(() => (groups.length ? baseRows.filter((r) => groups.includes(channelOf(r))) : baseRows), [baseRows, groups]);
    const statusRows = useMemo(() => (statuses.length ? filteredRows.filter((r) => statuses.includes(r.status)) : filteredRows), [filteredRows, statuses]);

    // v2.1: every payload row is listed (paginated), nothing is hidden; idle rows sit at the bottom of the revenue sort, dimmed.
    const idleRows = useMemo(() => statusRows.filter((r) => r._sev === "idle"), [statusRows]);
    const visibleRows = statusRows;
    const zeroRevCount = useMemo(() => visibleRows.filter((r) => !(Number(r.rev) > 0)).length, [visibleRows]);

    // Back to page 1 whenever the row set or the order changes (a page index past the end would show an empty grid).
    useEffect(() => { setPaginationModel((m) => (m.page === 0 ? m : {...m, page: 0})); }, [debouncedSearch, groups, providers, statuses, sortModel, from, to]);

    /** Yesterday's movers, restricted to the rows the filters keep (the API list is range-wide). */
    const changes = useMemo(() => {
        const list = (data && Array.isArray(data.changes)) ? data.changes : [];
        const keep = new Set(statusRows.map((r) => r.tag));
        return list.filter((c) => keep.has(c.tag));
    }, [data, statusRows]);

    const summary = useMemo(() => rollup(statusRows), [statusRows]);
    const groupRollups = useMemo(() => {
        const src = statuses.length ? baseRows.filter((r) => statuses.includes(r.status)) : baseRows;
        const by = new Map();
        src.forEach((r) => { const k = channelOf(r); if (!by.has(k)) by.set(k, []); by.get(k).push(r); });
        return Array.from(by.entries()).map(([k, rs]) => ({prefix: k, label: k, code: rs[0].type || null, ...rollup(rs, true)})).sort((a, b) => b.rev - a.rev);
    }, [baseRows, statuses]);

    const statusCounts = useMemo(() => {
        const c = {};
        filteredRows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
        return c;
    }, [filteredRows]);
    const attention = useMemo(() => statusRows.filter((r) => ALARM_STATUSES.includes(r.status))
        .sort((a, b) => (b._atRisk - a._atRisk) || (b.rev - a.rev)), [statusRows]);
    const overview = data && data.overview ? data.overview : null;
    const asOf = (overview && overview.as_of) || (data && data.summary && data.summary.yesterday && data.summary.yesterday.day) || null;
    asOfRef.current = asOf;
    const resyncProviders = useMemo(() => Array.from(new Set([...providerOptions, ...pulls.map((p) => p.provider).filter(Boolean)])).sort(), [providerOptions, pulls]);
    /** v3 blocks, restricted to the rows the filters keep. */
    const keepTag = useMemo(() => new Set(statusRows.map((r) => r.tag)), [statusRows]);
    const launches = useMemo(() => ((data && Array.isArray(data.launches)) ? data.launches : []).filter((l) => keepTag.has(l.tag)), [data, keepTag]);
    const silentProviders = useMemo(() => {
        const list = (data && Array.isArray(data.silent_providers)) ? data.silent_providers : [];
        return providers.length ? list.filter((sp) => providers.includes(sp.provider)) : list;
    }, [data, providers]);
    /** "What happened yesterday": one ranked feed (new alarms, silent partners, launches off pace, movers, ongoing, recovered). */
    const feed = useMemo(() => buildFeed({attention, rows: statusRows, changes, changeGroups: data && data.change_groups, silentProviders, launches, compareBase}),
        [attention, statusRows, changes, data, silentProviders, launches, compareBase]);
    const alarmTags = useMemo(() => new Set(attention.map((r) => r.tag)), [attention]);
    const pendingProviders = useMemo(() => (overview && overview.yesterday && Array.isArray(overview.yesterday.pending_providers) ? overview.yesterday.pending_providers : []), [overview]);

    const maxRev = useMemo(() => visibleRows.reduce((m, r) => Math.max(m, Number(r.rev) || 0), 0), [visibleRows]);
    const revDesc = useMemo(() => [...visibleRows].sort((a, b) => (b.rev || 0) - (a.rev || 0)), [visibleRows]);
    const totalRev = summary.rev;
    const sortedByRev = sortModel.length === 1 && sortModel[0].field === "rev" && sortModel[0].sort === "desc";
    /** 80 % cutoff on the revenue-sorted list: `count` tags make 80 % of revenue; `tag` = the rule row (only when the grid is sorted by revenue). */
    const cutoff = useMemo(() => {
        if (debouncedSearch || totalRev <= 0) return null;
        let cum = 0;
        for (let i = 0; i < revDesc.length; i++) {
            cum += revDesc[i].rev || 0;
            if (cum >= 0.8 * totalRev) return i < revDesc.length - 1 ? {tag: sortedByRev ? revDesc[i].tag : null, count: i + 1} : null;
        }
        return null;
    }, [sortedByRev, debouncedSearch, revDesc, totalRev]);


    // ------------------------------------------------------------------------------------------------ actions
    const applyPreset = useCallback((d) => { const r = presetRange(d); patch({preset: d, from: r.from, to: r.to}); }, [patch]);
    const applyRange = useCallback((f, t) => patch({preset: null, from: f, to: t}), [patch]);
    const toggleGroup = useCallback((p) => setState((s) => ({...s, groups: s.groups.includes(p) ? s.groups.filter((x) => x !== p) : [...s.groups, p]})), []);
    const toggleStatus = useCallback((st) => setState((s) => ({...s, statuses: s.statuses.length === 1 && s.statuses[0] === st ? [] : [st]})), []);
    const clearFilters = useCallback(() => patch({search: "", groups: [], providers: [], statuses: []}), [patch]);
    const selectTag = useCallback((tag) => patch({selectedTag: tag}), [patch]);
    const closeDrawer = useCallback(() => patch({selectedTag: null}), [patch]);
    const exportCsv = useCallback(() => {
        // allColumns: the grid hides Provider / CTR / RPM (days mode, narrow screens) through the visibility model; the CSV keeps them.
        if (apiRef.current && apiRef.current.exportDataAsCsv) apiRef.current.exportDataAsCsv({fileName: `traffic-control-${from}_${to}`, utf8WithBom: true, allColumns: true});
    }, [apiRef, from, to]);

    const selectedRow = useMemo(() => rows.find((r) => r.tag === state.selectedTag) || null, [rows, state.selectedTag]);
    const filterProvider = useCallback((p) => { patch({providers: [p]}); setTableOpen(true); }, [patch, setTableOpen]);
    const groupLabelOf = useCallback((prefix, row) => (groupOptions.find((g) => g.prefix === prefix) || {}).label || (row && row.group) || prefix, [groupOptions]);

    /** Ordered tag list the drawer steps through: grid order on ≥ 600, the row's own section on phone. */
    const navTags = useCallback(() => {
        if (!selectedRow) return [];
        if (!isPhone) {
            const ids = apiRef.current && apiRef.current.getSortedRowIds ? apiRef.current.getSortedRowIds() : null;
            return ids && ids.length ? ids : visibleRows.map((r) => r.tag);
        }
        const sk = SORT_KEYS.find((k) => k.key === sort.key) || SORT_KEYS[0];
        const dir = sort.dir === "asc" ? 1 : -1;
        const sectionOf = (r) => (state.groupBy === "group" ? channelOf(r) : state.groupBy === "none" ? "all"
            : ALARM_STATUSES.includes(r.status) ? "attention" : r.status === "OK" ? "healthy" : INFO_STATUSES.includes(r.status) ? "info" : "idle");
        const sec = sectionOf(selectedRow);
        if (sec === "attention" && sort.key === "rev" && sort.dir === "desc") {
            return statusRows.filter((r) => sectionOf(r) === sec)
                .sort((a, b) => ((b._atRisk || 0) - (a._atRisk || 0)) || (b.rev - a.rev)).map((r) => r.tag);
        }
        return statusRows.filter((r) => sectionOf(r) === sec).sort((a, b) => {
            const va = sk.get(a, {compareBase}); const vb = sk.get(b, {compareBase});
            if (isNilValue(va) && isNilValue(vb)) return b.rev - a.rev;
            if (isNilValue(va)) return 1;
            if (isNilValue(vb)) return -1;
            return va === vb ? b.rev - a.rev : (va > vb ? 1 : -1) * dir;
        }).map((r) => r.tag);
    }, [selectedRow, isPhone, apiRef, visibleRows, sort, state.groupBy, statusRows, compareBase]);
    const step = useCallback((delta) => {
        const list = navTags();
        const i = list.indexOf(state.selectedTag);
        if (i < 0) return;
        const next = list[i + delta];
        if (next) patch({selectedTag: next});
    }, [navTags, state.selectedTag, patch]);
    const navList = selectedRow ? navTags() : [];
    const navIndex = navList.indexOf(state.selectedTag);

    const activeFilters = (groups.length ? 1 : 0) + (providers.length ? 1 : 0) + (statuses.length ? 1 : 0) + (includeInactive ? 1 : 0);
    const filterActive = activeFilters > 0 || Boolean(debouncedSearch);
    const refetching = loading && Boolean(data);
    const firstLoad = loading && !data;

    /** Tags the filter-sheet draft would list (every row counts; nothing is hidden any more). */
    const countFor = useCallback((draft) => rows.filter((r) => (draft.providers.length === 0 || draft.providers.includes(r.provider)) && matchesSearch(r, debouncedSearch)
        && (draft.groups.length === 0 || draft.groups.includes(r.prefix)) && (draft.statuses.length === 0 || draft.statuses.includes(r.status))).length, [rows, debouncedSearch]);

    const drawer = (
        <TagDrawer row={selectedRow} open={Boolean(selectedRow)} onClose={closeDrawer} variant={variant} days={rangeDays} compareBase={compareBase} onResync={resyncFor}
                   onPrev={() => step(-1)} onNext={() => step(1)} hasPrev={navIndex > 0} hasNext={navIndex >= 0 && navIndex < navList.length - 1}/>
    );
    // The table opens on its own when a search / filter is active (the reader is looking for a tag).
    const tableShown = tableOpen || filterActive;
    const todayLine = data && data.today ? <TodayLine today={data.today} asOf={asOf} phone={isPhone} sx={{mt: isPhone ? 0.5 : 0.25, ml: isPhone ? 0 : 2}}/> : null;
    const heatmap = !firstLoad && (
        <Heatmap rows={visibleRows} days={days} metric={hmMetric} onMetric={setHmMetric} mode={hmMode} onMode={setHmMode}
                 preset={state.preset || rangeDays} onPreset={applyPreset} onSelectTag={selectTag} selectedTag={state.selectedTag}
                 launches={launches} pendingProviders={pendingProviders} groupLabel={groupLabelOf} phone={isPhone} sheetOpen={hmSheet} onSheetClose={() => setHmSheet(false)}/>
    );

    // ------------------------------------------------------------------------------------------------ phone
    if (isPhone) {
        return (
            <MainLayout>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <GlobalStyles styles={{body: {overscrollBehaviorY: "contain"}, main: {overscrollBehaviorY: "contain"}}}/>
                    <Box sx={{bgcolor: "background.paper", minHeight: "100%", pb: 3, ...TABULAR}}>
                        <PhoneHeader range={range || {from, to}} preset={state.preset} onPreset={applyPreset} onRange={applyRange}
                                     search={state.search} onSearch={(v) => patch({search: v})} activeFilters={activeFilters}
                                     onOpenFilters={() => setFilterOpen(true)} onRefresh={() => setRefreshKey((k) => k + 1)} loading={loading}/>
                        {error && <Alert severity="error" sx={{m: 1.5}}>{error}</Alert>}
                        <Box sx={{opacity: refetching ? 0.6 : 1, transition: "opacity .2s"}}>
                            {firstLoad ? (
                                <Box sx={{height: 112}}/>
                            ) : (
                                <OverviewBand phone overview={overview} summary={summary} range={range} days={rangeDays} series={series}
                                              compareBase={compareBase} onCompareBase={setCompareBase} onResync={resyncFor} showYesterday={phoneView === "triage"}
                                              rowCount={visibleRows.length} cutoffCount={cutoff ? cutoff.count : null} zeroCount={zeroRevCount}>
                                    {todayLine}
                                </OverviewBand>
                            )}
                            {/* Row 3 (44 px, sticky under the header): Feed | Tags | Heatmap + the tab's one control (sort · heatmap settings). */}
                            <Box sx={{position: "sticky", top: PHONE_HEADER_H, zIndex: 3, height: PHONE_TABS_H, boxSizing: "border-box", bgcolor: "background.paper", borderBottom: `1px solid ${NEUTRAL.hairline}`, borderTop: `1px solid ${NEUTRAL.hairline}`, px: 1, display: "flex", alignItems: "center", gap: 1}}>
                                <ToggleButtonGroup exclusive size="small" value={phoneView} onChange={(e, v) => v && setPhoneView(v)} aria-label="Phone view" sx={{flex: "1 1 auto", minWidth: 0,
                                                   "& .MuiToggleButton-root": {flex: 1, height: 34, px: 0.5, fontSize: 12.5, fontWeight: 600, textTransform: "none", lineHeight: 1, whiteSpace: "nowrap", color: "text.secondary", borderColor: NEUTRAL.divider, "&.Mui-selected": {color: "primary.main", bgcolor: "rgba(25,118,210,.08)"}}}}>
                                    <ToggleButton value="triage" aria-label="What happened">Feed{feed.digest.newCount ? ` · ${feed.digest.newCount}` : ""}</ToggleButton>
                                    <ToggleButton value="tags" aria-label="All tags">Tags</ToggleButton>
                                    <ToggleButton value="heatmap" aria-label="Heatmap">Heatmap</ToggleButton>
                                </ToggleButtonGroup>
                                {phoneView === "tags" && <SortChip sort={sort} onSort={setSort}/>}
                                {phoneView === "heatmap" && (
                                    <Button size="small" variant="outlined" color="inherit" onClick={() => setHmSheet(true)} aria-label="Heatmap settings" aria-haspopup="dialog"
                                            sx={{height: 32, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: NEUTRAL.divider, color: "text.primary", px: 1, whiteSpace: "nowrap", ...TABULAR, minWidth: 0}}>
                                        {(HEAT_METRICS.find((x) => x.key === hmMetric) || HEAT_METRICS[0]).short} · {hmMode === "d1" ? "day before" : "7d avg"} ▾
                                    </Button>
                                )}
                            </Box>
                            <PullBanner requests={pulls}/>
                            <ResyncButton phone variant="none" providers={resyncProviders} pendingProviders={pendingProviders} onRequest={requestResync} prefill={resyncPrefill} onPrefillConsumed={() => setResyncPrefill(null)} recent={pulls}/>
                            {!firstLoad && phoneView === "triage" && (
                                <PhoneTriage feed={feed} launches={launches} rangeDays={rangeDays} compareBase={compareBase} asOf={asOf} overview={overview}
                                             onSelect={selectTag} selectedTag={state.selectedTag} counts={statusCounts} statuses={statuses} onToggleStatus={(st) => { toggleStatus(st); setPhoneView("tags"); }}
                                             onFilterProvider={(p) => { patch({providers: [p]}); setPhoneView("tags"); }} onResync={resyncFor} alarmTags={alarmTags} earnedTags={overview && overview.earned_tags} pct={overview && overview.yesterday && overview.yesterday.pct}/>
                            )}
                            {phoneView === "tags" && (
                                <>
                                    {!firstLoad && <GroupsPanel phone groups={groupRollups} selected={groups} onToggle={toggleGroup} range={range}/>}
                                    <TagListMobile rows={visibleRows.filter((r) => r._sev !== "idle")} idleRows={idleRows} rangeDays={rangeDays}
                                                   sort={sort} onSort={setSort} onSelect={selectTag} selectedTag={state.selectedTag}
                                                   groupBy={state.groupBy} search={debouncedSearch} filterActive={filterActive}
                                                   totalCount={rows.length} onClear={clearFilters} compareBase={compareBase} asOf={asOf}/>
                                </>
                            )}
                            {phoneView === "heatmap" && <Box sx={{p: 1}}>{heatmap}</Box>}
                        </Box>
                    </Box>
                    <FilterSheet open={filterOpen} onClose={() => setFilterOpen(false)}
                                 value={{preset: state.preset, statuses, groups, providers, groupBy: state.groupBy, includeInactive, compareBase}}
                                 onApply={(v) => {
                                     const p = {statuses: v.statuses, groups: v.groups, providers: v.providers, groupBy: v.groupBy, includeInactive: Boolean(v.includeInactive)};
                                     if (v.preset && v.preset !== state.preset) { const r = presetRange(v.preset); Object.assign(p, {preset: v.preset, from: r.from, to: r.to}); }
                                     patch(p);
                                     if (v.compareBase && v.compareBase !== compareBase) setCompareBase(v.compareBase);
                                 }}
                                 statusCounts={statusCounts} groupOptions={groupRollups.map((g) => ({prefix: g.prefix, label: g.label, rev: g.rev}))}
                                 providerOptions={providerOptions} countFor={countFor}/>
                    {drawer}
                    <Snackbar open={Boolean(toast)} autoHideDuration={8000} onClose={() => setToast(null)} message={toast ? `Re-synced ${toast}` : ""}/>
                </LocalizationProvider>
            </MainLayout>
        );
    }

    // ------------------------------------------------------------------------------------------------ desktop / tablet
    const sortField = sortModel[0] ? sortModel[0].field : null;
    const sortText = sortField ? `sorted by ${SORT_LABEL[sortField] || sortField}${sortModel[0].sort === "asc" ? " ↑" : ""}` : "unsorted";

    return (
        <MainLayout>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Container maxWidth={false} sx={{px: {xs: 1.5, md: 3}, py: 2}}>
                    <Stack spacing={1.5}>
                        <Box>
                            <HeaderBar range={range || {from, to}} preset={state.preset} onPreset={applyPreset} onRange={applyRange}
                                       search={state.search} onSearch={(v) => patch({search: v})}
                                       groupOptions={groupOptions} groups={groups} onGroups={(v) => patch({groups: v})}
                                       providerOptions={providerOptions} providers={providers} onProviders={(v) => patch({providers: v})}
                                       includeInactive={includeInactive} onIncludeInactive={(v) => patch({includeInactive: v})}
                                       onExport={exportCsv} canExport={visibleRows.length > 0} view={view} onView={setView}
                                       extra={<ResyncButton providers={resyncProviders} pendingProviders={pendingProviders} onRequest={requestResync} prefill={resyncPrefill} onPrefillConsumed={() => setResyncPrefill(null)} recent={pulls}/>}/>
                            <PullBanner requests={pulls} sx={{mt: 1, borderRadius: 1, border: `1px solid ${NEUTRAL.hairline}`}}/>
                            <Box sx={{height: 2, mt: 1}}>{loading && <LinearProgress sx={{height: 2}}/>}</Box>
                        </Box>

                        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

                        <Box sx={{opacity: refetching ? 0.6 : 1, transition: "opacity .2s"}}>
                            <Stack spacing={1.5}>
                                {!firstLoad && (
                                    <OverviewBand overview={overview} summary={summary} range={range} days={rangeDays} series={series}
                                                  compareBase={compareBase} onCompareBase={setCompareBase} onResync={resyncFor}>
                                        {todayLine}
                                    </OverviewBand>
                                )}

                                {view === "heatmap" ? heatmap : (
                                    <>
                                        {!firstLoad && (
                                            <Box sx={{display: "grid", gridTemplateColumns: stackPanels ? "1fr" : "minmax(0, 3fr) minmax(0, 2fr)", gap: 1.5, alignItems: "flex-start"}}>
                                                <Stack spacing={1.5} sx={{minWidth: 0}}>
                                                    <FeedPanel feed={feed} asOf={asOf} days={rangeDays} compareBase={compareBase} counts={statusCounts} statuses={statuses}
                                                               onToggleStatus={(st) => { toggleStatus(st); setTableOpen(true); }} onSelect={selectTag} selectedTag={state.selectedTag}
                                                               overview={!filterActive ? overview : null} launches={launches} onFilterProvider={filterProvider} onResync={resyncFor} lastSeen={lastSeen} hideOngoing={!stackPanels}
                                                               earnedTags={overview && overview.earned_tags} pct={overview && overview.yesterday && overview.yesterday.pct}/>
                                                    <LaunchesPanel launches={launches} onSelect={selectTag} selectedTag={state.selectedTag} alarmTags={alarmTags}/>
                                                </Stack>
                                                <Stack spacing={1.5} sx={{minWidth: 0}}>
                                                    <GroupsPanel groups={groupRollups} selected={groups} onToggle={toggleGroup} range={range}/>
                                                    {!stackPanels && <OpenAlarmsCard item={feed.items.find((it) => it.kind === "ongoing") || null} days={rangeDays} onSelect={selectTag} selectedTag={state.selectedTag} launches={launches}/>}
                                                </Stack>
                                            </Box>
                                        )}

                                        <Box>
                                            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{columnGap: 1.5, rowGap: 0.75, mb: 0.75, minHeight: 32}}>
                                                {firstLoad ? (
                                                    <Typography component="h2" sx={{...TYPE.caption, fontSize: 12}}>Loading…</Typography>
                                                ) : (
                                                    <Box component="h2" sx={{m: 0, flex: "1 1 420px", minWidth: 0, fontWeight: 400, display: "flex", alignItems: "baseline", flexWrap: "wrap", columnGap: 1}}>
                                                        {tableShown ? (
                                                            <RangeCaption s={summary} range={range} days={rangeDays} count={visibleRows.length}
                                                                          cutoffCount={cutoff ? cutoff.count : null} zeroCount={zeroRevCount} wrap
                                                                          sx={{whiteSpace: "normal", lineHeight: 1.4}}/>
                                                        ) : (
                                                            // Collapsed table: one summary line and the button that opens it (state persisted).
                                                            <Typography component="span" sx={{...TYPE.caption, fontSize: 12, ...TABULAR}}>
                                                                <Box component="span" sx={{fontWeight: 600, color: "text.primary"}}>All tags</Box>
                                                                {" · "}{fmtInt(visibleRows.length)} tags · {fmtMoneyShort(summary.rev)}
                                                                {cutoff && cutoff.count > 0 ? ` · ${cutoff.count} tags = 80%` : ""}
                                                                {zeroRevCount > 0 ? ` · ${zeroRevCount} earned $0` : ""}
                                                            </Typography>
                                                        )}
                                                        {tableShown && (statusRows.length !== rows.length || sortField !== "rev" || sortModel[0].sort !== "desc") && (
                                                            <Typography component="span" sx={{...TYPE.caption, fontSize: 12, display: "block", ...TABULAR}}>
                                                                {statusRows.length !== rows.length && <>showing {statusRows.length} of {rows.length} · </>}{sortText}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    <Typography component="span" sx={{...TYPE.eyebrow}}>Money</Typography>
                                                    <SeriesToggle value={series} onChange={setSeries}/>
                                                    {tableShown && (
                                                        <>
                                                            <Typography component="span" sx={{...TYPE.eyebrow, ml: 1}}>Columns</Typography>
                                                            <ToggleButtonGroup exclusive size="small" value={state.trendMode} onChange={(e, v) => v && patch({trendMode: v})} aria-label="Columns"
                                                                               sx={{"& .MuiToggleButton-root": {height: CONTROL_H, px: 1.25, fontSize: 12.5, fontWeight: 600, textTransform: "none", lineHeight: 1, color: "text.secondary", borderColor: NEUTRAL.divider, whiteSpace: "nowrap", "&.Mui-selected": {color: "primary.main", bgcolor: "rgba(25,118,210,.08)"}}}}>
                                                                <ToggleButton value="bars" aria-label="7-day bars">{rangeDays}-day bars</ToggleButton>
                                                                <ToggleButton value="yday" aria-label="yesterday">yesterday</ToggleButton>
                                                            </ToggleButtonGroup>
                                                        </>
                                                    )}
                                                    <HelpPopover label="Table" lines={TABLE_HELP}/>
                                                    {!firstLoad && (
                                                        <Button size="small" variant={tableShown ? "text" : "outlined"} onClick={() => setTableOpen(!tableShown)} disabled={!tableShown && filterActive}
                                                                aria-expanded={tableShown} sx={{textTransform: "none", fontSize: 12.5, height: CONTROL_H, whiteSpace: "nowrap"}}>
                                                            {tableShown ? "Fold table" : "Open table"}
                                                        </Button>
                                                    )}
                                                </Stack>
                                            </Stack>

                                            {(tableShown || firstLoad) && (
                                                <TagGrid rows={visibleRows} days={days} rangeDays={rangeDays} maxRev={maxRev} cutoffTag={cutoff ? cutoff.tag : null}
                                                         sortModel={sortModel} onSortModelChange={(m) => setSortModel(m.length ? m : DEFAULT_SORT_MODEL)}
                                                         trendMode={state.trendMode} dayMetric="rev" dayTotals={[]} apiRef={apiRef}
                                                         onSelectTag={selectTag} selectedTag={state.selectedTag} variant={variant} hideRpm={hideRpm && variant === "mid"}
                                                         firstLoad={firstLoad} onClearFilters={filterActive ? clearFilters : null} compareBase={compareBase} range={range}
                                                         paginationModel={paginationModel} onPaginationModelChange={setPaginationModel} pageSizeOptions={[PAGE_SIZE, 100]}/>
                                            )}
                                        </Box>
                                    </>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </Container>
                {drawer}
                <Snackbar open={Boolean(toast)} autoHideDuration={8000} onClose={() => setToast(null)} message={toast ? `Re-synced ${toast}` : ""}
                          anchorOrigin={{vertical: "bottom", horizontal: "center"}}/>
            </LocalizationProvider>
        </MainLayout>
    );
};

export default TrafficControl;
